import _ from "lodash";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  ORCA_ON_CHAIN_FETCH_BEGIN,
  ORCA_ON_CHAIN_FETCH_SUCCESS,
  ORCA_ON_CHAIN_FETCH_FAILURE,
} from "./constants";
import {
  WhirlpoolContext,
  ORCA_WHIRLPOOL_PROGRAM_ID,
  getAllPositionAccountsByOwner,
} from "@orca-so/whirlpools-sdk";
import { Connection, PublicKey } from "@solana/web3.js";
import * as Sentry from "@sentry/react";
import { SOLANA_RPC_URL } from "../configure";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { calculatePositionsValue } from "./OrcaService";
import axios from "axios";

const parsePositionData = (positions, result) => {
  for (let data of positions) {
    const positionData = data[1];
    const whirlpool = positionData.whirlpool.toString();
    const liquidity = positionData.liquidity;
    if (!result[whirlpool]) result[whirlpool] = [];
    result[whirlpool].push({
      liquidity,
      tickLowerIndex: positionData.tickLowerIndex,
      tickUpperIndex: positionData.tickUpperIndex,
    });
  }
};

export function fetchOrcaOnchainDetails(input, wallet) {
  return async (dispatch) => {
    dispatch({ type: ORCA_ON_CHAIN_FETCH_BEGIN });

    try {
      const ownerAddress = input.selectedUserAddress;
      const ownerPubKey = new PublicKey(ownerAddress);

      const connection = new Connection(SOLANA_RPC_URL, "confirmed");
      const ctx = WhirlpoolContext.from(
        connection,
        wallet,
        ORCA_WHIRLPOOL_PROGRAM_ID
      );
      const positionData = await getAllPositionAccountsByOwner({
        ctx,
        owner: ownerPubKey,
        includesPositions: true,
        includesBundledPositions: true,
        includesPositionsWithTokenExtensions: true,
      });

      const resultMap = {};
      parsePositionData(
        _.entries(positionData?.positionsWithTokenExtensions),
        resultMap
      );
      parsePositionData(_.entries(positionData?.positions), resultMap);

      for (let bundle of positionData?.positionBundles) {
        parsePositionData(_.entries(bundle.bundledPositions), resultMap);
      }
      const result = [];
      for (let poolAddress of _.keys(resultMap)) {
        const orcaPool = await axios.get(
          `https://api.orca.so/v2/solana/pools?addresses=${poolAddress}`,
          {
            withCredentials: false,
          }
        );
        const poolData = _.get(orcaPool, "data.data[0]", {});
        const positions = resultMap[poolAddress];
        const r = await calculatePositionsValue({ poolData, positions });
        result.push({ ...poolData, ...r });
      }

      dispatch({
        type: ORCA_ON_CHAIN_FETCH_SUCCESS,
        data: { solana: result },
      });
    } catch (error) {
      console.log("error: ", error);
      Sentry.captureException(error);
      dispatch({ type: ORCA_ON_CHAIN_FETCH_FAILURE });
    }
  };
}

export function useFetchOrcaOnchainDetails() {
  const dispatch = useDispatch();
  const { wallet } = useSolanaWallet();
  const {
    orcaOnchainDetails,
    fetchOrcaOnchainDetailsPending,
    fetchOrcaOnchainDetailsDone,
  } = useSelector(
    (state) => ({
      orcaOnchainDetails: state.metadesk.orcaOnchainDetails,
      fetchOrcaOnchainDetailsPending:
        state.metadesk.fetchOrcaOnchainDetailsPending,
      fetchOrcaOnchainDetailsDone: state.metadesk.fetchOrcaOnchainDetailsDone,
    }),
    shallowEqual
  );

  const boundAction = useCallback(
    (data) => dispatch(fetchOrcaOnchainDetails(data, wallet)),
    [dispatch]
  );

  return useMemo(
    () => ({
      orcaOnchainDetails,
      fetchOrcaOnchainDetails: boundAction,
      fetchOrcaOnchainDetailsDone,
      fetchOrcaOnchainDetailsPending,
    }),
    [
      orcaOnchainDetails,
      boundAction,
      fetchOrcaOnchainDetailsDone,
      fetchOrcaOnchainDetailsPending,
    ]
  );
}

export function reducer(state, action) {
  switch (action.type) {
    case ORCA_ON_CHAIN_FETCH_BEGIN:
      return { ...state, fetchOrcaOnchainDetailsPending: true };

    case ORCA_ON_CHAIN_FETCH_SUCCESS:
      return {
        ...state,
        orcaOnchainDetails: action.data,
        fetchOrcaOnchainDetailsDone: true,
        fetchOrcaOnchainDetailsPending: false,
      };

    case ORCA_ON_CHAIN_FETCH_FAILURE:
      return { ...state, fetchOrcaOnchainDetailsPending: false };

    default:
      return state;
  }
}
